/**
 * Hero Section Component
 */
export class Hero {
    constructor() {
        this.swiper = null;
        this.serviceSwiper = null;
        this.pipelineSwiper = null;
    }

    init() {
        this.initMainHero();
        this.initServiceSwiper();
        this.initPipelineSwiper();
        this.initTeamAnimation();
        this.initInteriorScroll();
    }

    initMainHero() {
        const el = document.getElementById("main-hero-swiper");
        if (!el) return;

        // Elisigen hero: Swiper with parallax + custom flex pagination
        if (el.classList.contains("no-main-hero__swiper")) {
            this.initElisigenHero(el);
            return;
        }

        const galleryItems = document.querySelectorAll(
            ".no-main-hero__gallery .no-main-hero__image"
        );

        const revealAt = (index) => {
            galleryItems.forEach((el, i) => {
                if (i === index) el.classList.add("is-revealing");
                else el.classList.remove("is-revealing");
            });
        };

        document
            .querySelectorAll("#main-hero-swiper .swiper-slide")
            .forEach(prepSlideSvg);

        seedObjectTransforms();

        this.swiper = new Swiper(el, {
            loop: true,
            effect: "fade",
            fadeEffect: { crossFade: true },
            speed: 1200,
            autoplay: { delay: 10000, disableOnInteraction: true },
            pagination: {
                type: "bullets",
                el: el.querySelector(".no-main-hero__pagination"),
                clickable: true,
            },
            navigation: {
                nextEl: el.querySelector(".no-main-hero__button--next"),
                prevEl: el.querySelector(".no-main-hero__button--prev"),
            },
            on: {
                init: (sw) => {
                    revealAt(sw.realIndex);
                    const activeSlide = el.querySelector(
                        ".swiper-slide-active"
                    );
                    resetSlideSvg(activeSlide);
                    drawSlideSvg(activeSlide);
                    playWiggleFor(activeSlide);
                },
                slideChangeTransitionStart: (sw) => {
                    revealAt(sw.realIndex);
                    el.querySelectorAll(".swiper-slide").forEach(resetSlideSvg);
                    const activeSlide = el.querySelector(
                        ".swiper-slide-active"
                    );
                    drawSlideSvg(activeSlide);
                    playWiggleFor(activeSlide);
                },
            },
        });
    }

    initElisigenHero(el) {
        const paginationSegments = el.querySelectorAll(
            ".no-main-hero__pagination-segment"
        );
        const paginationFills = el.querySelectorAll(
            ".no-main-hero__pagination-fill"
        );
        const autoplayDelay = 5000;
        let progressRafId = null;
        let progressStartAt = Date.now();

        const resetProgress = () => {
            if (progressRafId) cancelAnimationFrame(progressRafId);
            progressRafId = null;
            paginationFills.forEach((fill) => {
                if (fill) fill.style.width = "0%";
            });
        };

        const startProgressFor = (index) => {
            resetProgress();
            progressStartAt = Date.now();

            const fill = paginationFills[index];
            if (!fill) return;

            const tick = () => {
                const elapsed = Date.now() - progressStartAt;
                const ratio = Math.max(0, Math.min(1, elapsed / autoplayDelay));
                fill.style.width = `${ratio * 100}%`;
                progressRafId = requestAnimationFrame(tick);
            };
            tick();
        };

        const updatePagination = (index) => {
            paginationSegments.forEach((seg, i) => {
                seg.classList.toggle("is-active", i === index);
            });
            startProgressFor(index);
        };

        this.swiper = new Swiper(el, {
            loop: true,
            effect: "fade",
            fadeEffect: { crossFade: true },
            speed: 800,
            parallax: true,
            autoplay: { delay: 5000, disableOnInteraction: false },
            on: {
                init: (sw) => updatePagination(sw.realIndex),
                slideChangeTransitionStart: () => resetProgress(),
                slideChangeTransitionEnd: (sw) =>
                    updatePagination(sw.realIndex),
            },
        });

        paginationSegments.forEach((seg, i) => {
            seg.addEventListener("click", () => {
                this.swiper.slideToLoop(i);
            });
        });
    }

    initServiceSwiper() {
        const el = document.getElementById("main-service-swiper");
        if (!el) return;

        this.serviceSwiper = new Swiper(el, {
            slidesPerView: 3,
            spaceBetween: 80,
            speed: 1200,
            navigation: {
                nextEl: ".swiper-button-next",
                prevEl: ".swiper-button-prev",
            },
            breakpoints: {
                320: { slidesPerView: 1.25, spaceBetween: 20 },
                768: { slidesPerView: 1.5, spaceBetween: 40 },
                1024: { slidesPerView: 2.5, spaceBetween: 60 },
                1280: { slidesPerView: 3, spaceBetween: 80 },
            },
        });
    }

    initPipelineSwiper() {
        const el = document.getElementById("pipeline-swiper");
        if (!el || typeof Swiper === "undefined") return;

        const paginationEl = document.getElementById("pipeline-pagination");
        const slideCount = el.querySelectorAll(".swiper-slide").length;
        if (slideCount < 1) return;

        const options = {
            slidesPerView: 1.2,
            spaceBetween: 16,
            loop: false,
            speed: 1000,
            centeredSlides: false,
            grabCursor: true,
            navigation: {
                nextEl: ".no-main-pipeline__swiper-button-next",
                prevEl: ".no-main-pipeline__swiper-button-prev",
            },
            breakpoints: {
                320: { slidesPerView: 1.2, spaceBetween: 12 },
                544: { slidesPerView: 1.5, spaceBetween: 16 },
                768: { slidesPerView: 2, spaceBetween: 20 },
                1024: { slidesPerView: 2.5, spaceBetween: 24 },
                1280: { slidesPerView: 3, spaceBetween: 24 },
            },
        };

        if (paginationEl) {
            if (slideCount > 1) {
                options.pagination = {
                    el: paginationEl,
                    clickable: true,
                };
            } else {
                paginationEl.hidden = true;
            }
        }

        this.pipelineSwiper = new Swiper("#pipeline-swiper", options);
    }

    initTeamAnimation() {
        const peopleSection = document.querySelector(".no-main-team");
        const peopleSvg = document.querySelector(".no-main-team__line svg");
        if (!peopleSection || !peopleSvg) return;

        const elems = peopleSvg.querySelectorAll(
            "path, line, polyline, polygon, circle, ellipse, rect"
        );

        elems.forEach((el) => {
            let len = 0;
            if (typeof el.getTotalLength === "function") {
                len = el.getTotalLength();
            } else if (el.tagName === "rect") {
                const w = parseFloat(el.getAttribute("width")) || 0;
                const h = parseFloat(el.getAttribute("height")) || 0;
                len = 2 * (w + h);
            }
            el.dataset._dash = len;
            gsap.set(el, {
                strokeDasharray: len,
                strokeDashoffset: len,
            });
        });

        const tl = gsap.timeline({
            defaults: { ease: "none" },
            scrollTrigger: {
                trigger: peopleSection,
                start: "top 80%",
                end: "bottom bottom",
                scrub: true,
            },
        });

        elems.forEach((el, i) => {
            const len = parseFloat(el.dataset._dash) || 0;
            tl.to(el, { strokeDashoffset: 0, duration: 1 }, i * 0.08);
        });
    }

    initInteriorScroll() {
        const container = document.querySelector(".no-main-interior");
        const wrapper = document.querySelector(".no-main-interior__wrapper");
        const items = [...document.querySelectorAll(".no-main-interior__item")];

        if (!wrapper || items.length === 0) return;

        const getDistance = () => {
            const totalWidth =
                wrapper.scrollWidth +
                (window.innerWidth >= 768 ? window.innerWidth / 4 : 1);
            const viewportWidth = window.innerWidth;
            return totalWidth - viewportWidth;
        };

        gsap.timeline({
            scrollTrigger: {
                trigger: container,
                start: "top top",
                end: () => `+=${getDistance()}`,
                scrub: true,
                pin: true,
                anticipatePin: 1,
            },
        }).to(wrapper, {
            x: -getDistance(),
            ease: "none",
            duration: 5,
        });
    }
}
